/**
 * ``useTrackDetections`` — the members of a track that is **not** in the live
 * buffer (aged out of the live window, or reached cold from the Query panel),
 * fetched from ``GET /api/query/range/detections`` over the track's own span
 * with ``producer_ids`` + ``object_id`` (ADR-041; REQ-QUERY-052; REQ-UI-174).
 *
 * # Why this is analyst-gated and the live path is not
 *
 * The range route is the ad-hoc query surface — ``query_user`` per
 * REQ-AUTH-025, operator excluded. A live track's members come from the
 * shared object-track store instead (every read role), so the DETECTIONS
 * section only reaches for this hook when the store has nothing, and only
 * enables it when the session may call the route. An operator looking at an
 * aged-out track sees the members it can reach (none) and a note saying why,
 * never a 403 (CLAUDE.md §8.1 — the boundary is stated, not hidden).
 *
 * Newest first (the route's keyset order); one page of
 * :data:`TRACK_DETECTIONS_PAGE_SIZE` with a cursor for the next.
 *
 * # Why the page size is the *rendered* page size
 *
 * Until 2026-10-05 the hook fetched 100 and the section appended each
 * result to the last, so the DOM grew without bound and "page four" meant
 * walking pages two and three into it. The section now renders exactly one
 * page (REQ-UI-174 amended), so a request and a page are the same thing and
 * stepping back is a cache hit on a window the operator already disclosed
 * — no second audit event for a read they already made.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type RangeDetectionsResponse = components["schemas"]["RangeDetectionsResponse"];

/** The roles the BFF admits on the range route (REQ-AUTH-025: not operator). */
export const TRACK_DETECTIONS_READER_ROLES: readonly Role[] = [
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Detections per page, in the request **and** on screen (REQ-UI-174).
 *
 * Fifty: dense enough that a minute of a moving object is one page, short
 * enough that the section stays a section rather than taking over the pane.
 */
export const TRACK_DETECTIONS_PAGE_SIZE = 50;

export interface UseTrackDetectionsOptions {
  /** ``null`` disables the query (the live store already has the members). */
  readonly track: {
    readonly producer_id: string;
    readonly object_id: string;
    readonly first_time: string;
    readonly last_time: string;
  } | null;
  /** The keyset cursor for the next page; ``null`` for the first. */
  readonly cursor?: string | null;
}

/** Compose the range URL for one track's span. */
export function composeTrackDetectionsUrl(
  track: NonNullable<UseTrackDetectionsOptions["track"]>,
  cursor: string | null,
): string {
  // Widen the span by one second each way: the summary's bounds are the
  // members' own times, and the route's window is half-open.
  const start = new Date(Date.parse(track.first_time) - 1000).toISOString();
  const end = new Date(Date.parse(track.last_time) + 1000).toISOString();
  const params = new URLSearchParams({
    start_time: start,
    end_time: end,
    producer_ids: track.producer_id,
    object_id: track.object_id,
    limit: String(TRACK_DETECTIONS_PAGE_SIZE),
  });
  if (cursor !== null) {
    params.set("cursor", cursor);
  }
  return `/api/query/range/detections?${params.toString()}`;
}

export function useTrackDetections(
  options: UseTrackDetectionsOptions,
): UseQueryResult<RangeDetectionsResponse, ApiError> & { readonly canQuery: boolean } {
  const session = useSession();
  const canQuery = session.hasAnyRole(TRACK_DETECTIONS_READER_ROLES);
  const { track } = options;
  const cursor = options.cursor ?? null;
  const enabled = track !== null && canQuery;

  const query = useQuery<RangeDetectionsResponse, ApiError>({
    queryKey:
      track !== null
        ? ["track-detections", track.producer_id, track.object_id, cursor]
        : ["track-detections", "disabled"],
    queryFn: async ({ signal }) => {
      if (track === null) {
        throw new ApiError(0, null, "useTrackDetections: query ran without a track");
      }
      const raw = await apiFetch<unknown>(composeTrackDetectionsUrl(track, cursor), { signal });
      if (!isRangeDetectionsResponse(raw)) {
        console.error(
          "[meridian/entity-details/use-track-detections] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "range-detections response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
  return Object.assign(query, { canQuery });
}

function isRangeDetectionsResponse(value: unknown): value is RangeDetectionsResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.items) && (obj.next_cursor === null || typeof obj.next_cursor === "string")
  );
}
